import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { v4 as uuid } from 'uuid';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useEventLogging } from '../../../../models/Logging';
import { numberToCurrencyStringValue, openUrlInNewTab } from '../../../../models/UiUtils';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateAllDonationFilters,
	invalidateAllDonorFilters,
	invalidateDonationFilter,
	invalidateDonorFilter,
	useBulkDeleteDonationsMutation,
	useDeleteDonationMutation,
	useDonationContactsMutation,
	useDonationFilterQuery,
	useDonationSummaryQuery,
	useDonorContactsMutation,
	useDonorFilterQuery,
	useDonorSummaryQuery,
	useExportDonationsByDonorsMutation,
	useExportDonationsMutation,
} from '../../../../queries/';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { ExpandableText } from '../../../components-v2/ExpandableText';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Column, Page } from '../../../components-v2/PageLayout';
import { Pill } from '../../../components-v2/Pill/Pill';
import { Checkbox } from '../../../components/Checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import {
	Databoard,
	DataboardActions,
	DataboardBody,
	DataboardFilters,
	DataboardNoResults,
	DataboardStat,
	DataboardStatLabel,
	DataboardStatTitle,
	DataboardStatValue,
	DataboardStats,
} from '../../../components/Databoard';

import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { ExportConfirmationModalV2 } from '../../../components/ExportConfirmation';
import { FabContext } from '../../../components/FabContext';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../components/HoverCard';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { TextInput } from '../../../components/TextInput';
import { UsersActionBar } from '../../../components/UsersActionBar';
import { styleSheet as simpleAutoCompleteStyleSheet } from '../../../components/autocomplete/SimpleAutoCompleteSearchField/styles';
import { EntityChip } from '../../../components/chips/EntityChip';
import { Badge } from '../../../components/dataBoards/Badge';
import { DeleteModal, DeleteType } from '../../../components/dataBoards/DeleteModal';
import { AmountFilterDropdown } from '../../../components/dataBoards/donations/AmountFilterDropdown';
import { DeleteDonationsConfirmModal } from '../../../components/dataBoards/donations/DeleteDonationsConfirmModal';
import {
	DeletePreviousImportModal,
	DeletePreviousImportRange,
} from '../../../components/dataBoards/donations/DeletePreviousImportModal';
import { DonationDateDropdown } from '../../../components/dataBoards/donations/DonationDateDropdown';
import { DonationCampaignSearch } from '../../../components/dataBoards/donations/DonationCampaignSearch';
import { DonationSearchDescription } from '../../../components/dataBoards/donations/DonationSearchDescription';
import { DonationTagSearch } from '../../../components/dataBoards/donations/DonationTagSearch';
import { DonorDetailsFlyout } from '../../../components/dataBoards/donations/DonorDetailsFlyout';
import { EditDonationModal } from '../../../components/dataBoards/donations/EditDonationModal';
import { TaxLetterModal } from '../../../components/dataBoards/donations/TaxLetterModal';
import { AmountFilterOption, DonationPropertyOption } from '../../../components/dataBoards/donations/models';
import { SmallGraph } from '../../../components/svgs/graphics/SmallGraphGraphic';
import { DoubleArrowIcon } from '../../../components/svgs/icons/DoubleArrowIcon';
import { DropDownIcon } from '../../../components/svgs/icons/DropDownIcon';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { ROUTES } from '../../../constants/routes';
import { brandSecondary, white } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../styles';
import { styleSheet } from './styles';
import { THEME } from '../../../components-v2/Theme/theme';

export const DonationPropertyOptions = Object.keys(DonationPropertyOption).map(key => {
	const option = {
		id: key,
		text: DonationPropertyOption[key as keyof typeof DonationPropertyOption],
		dataContext: key,
	};
	return option;
});

enum MenuItem {
	Edit = 'Edit',
	Delete = 'Delete',
	TaxLetter = 'Tax Letter',
}

export function DonationsBoard() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const { logApiError } = useEventLogging('DonationsBoard');
	const [key] = React.useState(uuid());

	const [selectedSearchProperty, setSelectedSearchProperty] = React.useState(DonationPropertyOptions[0]);
	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const debouncedSearchFieldValue = useDebounceValue(searchFieldValue, 500);
	const [selectedCampaign, setSelectedCampaign] = React.useState<Api.IDonationCampaign>(null);
	const [selectedTag, setSelectedTag] = React.useState<Api.IAccountTag>(null);

	const [selectedAmountFilter, setSelectedAmountFilter] = React.useState<AmountFilterOption>(
		AmountFilterOption.OneTime
	);
	const [minimumDonation, setMinimumDonation] = React.useState<string>('');
	const [maximumDonation, setMaximumDonation] = React.useState<string>('');

	const [hasDonatedMinDate, setHasDonatedMinDate] = React.useState<string | null>(null);
	const [hasDonatedMaxDate, setHasDonatedMaxDate] = React.useState<string | null>(null);
	const [notDonatedMinDate, setNotDonatedMinDate] = React.useState<string | null>(null);
	const [notDonatedMaxDate, setNotDonatedMaxDate] = React.useState<string | null>(null);
	const [isFirstDonation, setIsFirstDonation] = React.useState<boolean>(false);
	const [creationDateRange, setCreationDateRange] = React.useState<DeletePreviousImportRange | null>(null);
	const [isDeletePreviousImportOpen, setIsDeletePreviousImportOpen] = React.useState(false);
	const [isBulkDeleteOpen, setIsBulkDeleteOpen] = React.useState(false);

	const [selectedViewMode, setSelectedViewMode] = React.useState<'donors' | 'donations'>('donors');
	const [userHasSelectedViewMode, setUserHasSelectedViewMode] = React.useState<boolean>(false);

	const [donationToEdit, setDonationToEdit] = React.useState<Api.IDonation>(null);
	const [donationToDelete, setDonationToDelete] = React.useState<Api.IDonation>(null);
	const [contactIdOfDonorToView, setContactIdOfDonorToView] = React.useState<string>(null);
	const [isEditDonationOpen, setIsEditDonationOpen] = React.useState(false);
	const [isDeleteDonationOpen, setIsDeleteDonationOpen] = React.useState(false);
	const [isViewDonationOpen, setIsViewDonationOpen] = React.useState(false);
	const [taxLetterTarget, setTaxLetterTarget] = React.useState<{
		contactId: string;
		donationIds?: string[];
	}>(null);
	const [bulkTaxLetterContactIds, setBulkTaxLetterContactIds] = React.useState<string[]>(null);

	const isFilteringLifetime = selectedAmountFilter === AmountFilterOption.Lifetime;
	const hasNotDonatedFilter = !!notDonatedMinDate;

	// Determine available view modes based on current filters
	const availableViewModes = React.useMemo(() => {
		// Tags only exist on donations, not donors
		if (selectedTag) {
			return ['donations'] as const;
		}
		// Lifetime and Not Donated filters only make sense for donors
		if (isFilteringLifetime || hasNotDonatedFilter) {
			return ['donors'] as const;
		}
		// Otherwise both options are available
		return ['donors', 'donations'] as const;
	}, [isFilteringLifetime, hasNotDonatedFilter, selectedTag]);

	// Smart default logic - only applies when user hasn't manually selected
	const shouldDefaultToDonations = React.useMemo(() => {
		// If only donors are available, default to donors
		if (availableViewModes.length === 1 && availableViewModes[0] === 'donors') {
			return false; // Show donors
		}

		// Donor name only (no other filters) → show donors
		const isDonorNameOnly =
			debouncedSearchFieldValue &&
			selectedSearchProperty.text === DonationPropertyOption.DonorName &&
			!selectedCampaign &&
			!selectedTag &&
			!minimumDonation &&
			!maximumDonation &&
			!hasDonatedMinDate &&
			!notDonatedMinDate;

		if (isDonorNameOnly) {
			return false; // Show donors
		}

		// Default to donations for most other cases
		return true;
	}, [
		availableViewModes,
		debouncedSearchFieldValue,
		selectedSearchProperty,
		selectedCampaign,
		selectedTag,
		minimumDonation,
		maximumDonation,
		hasDonatedMinDate,
		notDonatedMinDate,
	]);

	// Only apply default logic if user hasn't manually selected
	React.useEffect(() => {
		if (!userHasSelectedViewMode) {
			const newMode = shouldDefaultToDonations ? 'donations' : 'donors';
			setSelectedViewMode(newMode);
		}
		// If current selection is not available, force it to donors
		else if (!availableViewModes.includes(selectedViewMode)) {
			setSelectedViewMode('donors');
		}
	}, [shouldDefaultToDonations, userHasSelectedViewMode, availableViewModes, selectedViewMode]);

	const isDonorMode = selectedViewMode === 'donors';
	const amountFilterDropdownText = React.useMemo(() => {
		if (!!selectedAmountFilter && !minimumDonation && !maximumDonation) {
			return 'Amount';
		}
		const duration = selectedAmountFilter === AmountFilterOption.OneTime ? '1-Time' : 'Lifetime';

		return `$${minimumDonation ?? '0'}${maximumDonation ? '-' + maximumDonation : '+'}/${duration}`;
	}, [selectedAmountFilter, minimumDonation, maximumDonation]);

	React.useEffect(() => {
		resetDonationSelection();
		resetDonorselection();
		// Invalidate both query caches when switching between donor/donation modes
		invalidateAllDonationFilters();
		invalidateAllDonorFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDonorMode]);

	// Unified filter criteria that works for both donation and donor endpoints
	const unifiedFilterRequest = React.useMemo(() => {
		// Typed as any[] because we mix DonationFilterProperty and DonorFilterProperty values,
		// and use string literal ops ('Gt', 'Lt', 'Not') that match FilterOperator but aren't typed as such.
		const criteria: any[] = [];

		// Add name filter (works for both endpoints)
		if (debouncedSearchFieldValue && selectedSearchProperty.text === DonationPropertyOption.DonorName) {
			criteria.push({
				property: 'Name',
				value: debouncedSearchFieldValue,
			});
		}

		// Amount filter - use different properties based on mode
		if (minimumDonation || maximumDonation) {
			const amountProperty = isFilteringLifetime ? 'Lifetime' : 'Amount';

			if (minimumDonation) {
				criteria.push({
					property: amountProperty,
					op: 'Gt',
					value: minimumDonation,
				});
			}
			if (maximumDonation) {
				criteria.push({
					property: amountProperty,
					op: 'Lt',
					value: maximumDonation,
				});
			}
		}

		// "Has Donated" date filter
		if (hasDonatedMinDate) {
			criteria.push({
				property: 'Date',
				op: 'Gt',
				value: hasDonatedMinDate,
			});
			if (hasDonatedMaxDate) {
				criteria.push({
					property: 'Date',
					op: 'Lt',
					value: hasDonatedMaxDate,
				});
			}
		}

		// "Has NOT Donated" date filter
		if (notDonatedMinDate) {
			if (notDonatedMaxDate) {
				// Date range: "did not donate between X and Y"
				criteria.push({
					property: 'LastDonationDate',
					op: 'Not',
					criteria: [
						{ op: 'Gt', value: notDonatedMinDate },
						{ op: 'Lt', value: notDonatedMaxDate },
					],
				});
			} else {
				// Legacy format: single-date "not donated since X" from saved URLs or old API payloads
				criteria.push({
					property: 'LastDonationDate',
					op: 'Lt',
					value: notDonatedMinDate,
				});
			}
		}

		// FirstTimeDonor filter
		if (isFirstDonation && hasDonatedMinDate) {
			criteria.push({
				property: 'FirstTimeDonor',
				value: 'true',
			});
		}

		// Campaign filter
		if (selectedCampaign?.id) {
			criteria.push({
				property: 'Campaign',
				value: selectedCampaign.id,
			});
		}

		// Tag filter - add to criteria array
		if (selectedTag?.name) {
			criteria.push({
				property: 'Tag',
				value: selectedTag.name,
			});
		}

		// Creation-date filter - set by the "Delete previous import" flow
		if (creationDateRange) {
			criteria.push({
				property: Api.DonationFilterProperty.CreationDate,
				op: Api.FilterOperator.Gt,
				value: creationDateRange.startIso,
			});
			criteria.push({
				property: Api.DonationFilterProperty.CreationDate,
				op: Api.FilterOperator.Lt,
				value: creationDateRange.endIso,
			});
		}

		return {
			criteria,
		};
	}, [
		debouncedSearchFieldValue,
		selectedSearchProperty,
		minimumDonation,
		maximumDonation,
		isFilteringLifetime,
		hasDonatedMinDate,
		hasDonatedMaxDate,
		notDonatedMinDate,
		notDonatedMaxDate,
		isFirstDonation,
		selectedCampaign,
		selectedTag,
		creationDateRange,
	]);

	const donorFilterQuery = useDonorFilterQuery({
		enabled: isDonorMode && !!unifiedFilterRequest,
		filterRequest: unifiedFilterRequest,
	});
	const donors = React.useMemo(() => {
		return donorFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [donorFilterQuery.data?.pages]);

	const donorSummaryQuery = useDonorSummaryQuery({
		enabled: isDonorMode,
		filterRequest: unifiedFilterRequest,
	});

	const donationFilterQuery = useDonationFilterQuery({
		enabled: !isDonorMode && !!unifiedFilterRequest,
		filterRequest: unifiedFilterRequest,
		expand: 'contact,TagsCollection',
	});

	const donationSummaryQuery = useDonationSummaryQuery({
		enabled: !isDonorMode,
		filterRequest: unifiedFilterRequest,
	});

	const summary = React.useMemo(() => {
		if (isDonorMode) {
			return donorSummaryQuery.data;
		}
		return donationSummaryQuery.data;
	}, [donationSummaryQuery.data, donorSummaryQuery.data, isDonorMode]);

	const refreshDonations = () => {
		setDonationToEdit(null);
		invalidateDonationFilter({
			filterRequest: unifiedFilterRequest,
		});
		invalidateDonorFilter({
			filterRequest: unifiedFilterRequest,
		});
	};

	const donationContactsMutation = useDonationContactsMutation();
	const donorContactsMutation = useDonorContactsMutation();
	const exportDonationsMutation = useExportDonationsMutation();
	const exportDonationsByDonorsMutation = useExportDonationsByDonorsMutation();
	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);
	const donations = React.useMemo(() => {
		return donationFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [donationFilterQuery.data?.pages]);

	const {
		excludedRows: excludedDonations,
		selectedRows: selectedDonations,
		selectionState: donationSelectionState,
		isAllSelected: isAllDonationsSelected,
		isPartialSelected: isDonationsPartialSelected,
		isSelectionStateAll: isDonationSelectionStateAll,
		isSelected: isDonationSelected,
		reset: resetDonationSelection,
		toggleRow: toggleDonationRow,
		toggleSelectAll: toggleSelectAllDonations,
	} = useSelectionState({ rows: donations?.map(d => d.id) });

	const {
		excludedRows: excludedDonors,
		selectedRows: selectedDonors,
		selectionState: donorselectionState,
		isAllSelected: isAllDonorsSelected,
		isPartialSelected: isDonorsPartialSelected,
		isSelectionStateAll: isDonorSelectionStateAll,
		isSelected: isDonorSelected,
		reset: resetDonorselection,
		toggleRow: toggleDonorRow,
		toggleSelectAll: toggleSelectAllDonors,
	} = useSelectionState({ rows: donors?.map(d => d.id) });

	const dedupeContacts = (contacts: Api.IProjectedContact[]) => {
		return contacts.filter((contact, index) => contacts.findIndex(c => c.id === contact.id) === index);
	};

	const donorExcludedContacts = React.useMemo(() => {
		return excludedDonors
			.map(donorId => {
				const donor = donors.find(d => d.contactId === donorId);

				return donor ? donor.contact : null;
			})
			.filter(Boolean) as Api.IProjectedContact[];
	}, [excludedDonors, donors]);

	const donationsExcludedContacts = React.useMemo(() => {
		return excludedDonations
			.map(donationId => {
				const donation = donations.find(d => d.id === donationId);

				return donation ? donation.contact : null;
			})
			.filter(Boolean) as Api.IProjectedContact[];
	}, [excludedDonations, donations]);

	const donorsSelectedContacts = React.useMemo(() => {
		return dedupeContacts(
			donors.filter(donor => selectedDonors.indexOf(donor.contactId) > -1).map(donor => donor.contact)
		);
	}, [donors, selectedDonors]);

	const donationsSelectedContacts = React.useMemo(() => {
		return dedupeContacts(
			donations.filter(donation => selectedDonations.indexOf(donation.id) > -1).map(donation => donation.contact)
		);
	}, [donations, selectedDonations]);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);

		let results: Api.IProjectedContact[] = [];

		if (isAllSelected) {
			try {
				let apiResults: Api.IProjectedContact[] = [];

				if (isDonorMode) {
					const excludedContactsCriteria = donorExcludedContacts.map(contact => ({
						property: 'ContactId',
						op: 'Not',
						value: contact.id,
					}));
					apiResults = await donorContactsMutation.mutateAsync({
						filterRequest: {
							criteria: [...unifiedFilterRequest.criteria, ...excludedContactsCriteria],
						},
					});
				} else {
					const excludedContactsCriteria = donationsExcludedContacts.map(contact => ({
						property: 'ContactId',
						op: 'Not',
						value: contact.id,
					}));

					apiResults = await donationContactsMutation.mutateAsync({
						filterRequest: {
							criteria: [...unifiedFilterRequest.criteria, ...excludedContactsCriteria],
						},
					});
				}
				results = apiResults;
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
			}
		} else {
			results = isDonorMode ? donorsSelectedContacts : donationsSelectedContacts;
		}

		setActionButtonsDisabled(false);
		return results;
	};

	const onToggleSelectAll = () => {
		resetDonationSelection();
		resetDonorselection();

		if (isDonorMode) {
			toggleSelectAllDonors();
		} else {
			toggleSelectAllDonations();
		}
	};

	const isAllSelected = React.useMemo(() => {
		if (isDonorMode) {
			return isAllDonorsSelected;
		}
		return isAllDonationsSelected;
	}, [isAllDonationsSelected, isAllDonorsSelected, isDonorMode]);

	const isPartialSelected = React.useMemo(() => {
		if (isDonorMode) {
			return isDonorsPartialSelected;
		}
		return isDonationsPartialSelected;
	}, [isDonationsPartialSelected, isDonorsPartialSelected, isDonorMode]);

	const clearSearch = () => {
		const donorNameOption = DonationPropertyOptions[0];
		setSelectedSearchProperty(donorNameOption);
		setSearchFieldValue('');
		setSelectedCampaign(null);
		setSelectedTag(null);
		setMinimumDonation('');
		setMaximumDonation('');
		setHasDonatedMinDate(null);
		setHasDonatedMaxDate(null);
		setNotDonatedMinDate(null);
		setNotDonatedMaxDate(null);
		setIsFirstDonation(false);
		setSelectedAmountFilter(AmountFilterOption.OneTime);
		setCreationDateRange(null);
		setSelectedViewMode('donors'); // Reset to default
		setUserHasSelectedViewMode(false); // Allow smart defaults to work again

		// Clear all cached query data to ensure fresh results
		invalidateAllDonationFilters();
		invalidateAllDonorFilters();
	};

	const onSearchFieldInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchFieldValue(e.target.value);
	};

	const addDonationClicked = () => {
		setDonationToEdit({} as Api.IDonation);
		setIsEditDonationOpen(true);
	};

	const editDonation = (donation: Api.IDonation) => {
		setDonationToEdit(donation);
		setIsEditDonationOpen(true);
	};

	const deleteDonation = (donation: Api.IDonation) => {
		setDonationToDelete(donation);
		setIsDeleteDonationOpen(true);
	};

	const importClicked = () => {
		history.push(`/dataBoards/donations/import`);
	};

	const selectSearchProperty = (selectedOption: (typeof DonationPropertyOptions)[number]) => {
		setSearchFieldValue('');
		setSelectedCampaign(null);
		setSelectedTag(null);
		setSelectedSearchProperty(selectedOption);
	};

	const selectDonationCampaign = (donationCampaign: Api.IDonationCampaign) => {
		setSelectedCampaign(donationCampaign);
	};

	const onCampaignSearchClear = () => {
		setSelectedCampaign(null);
	};

	const viewDetails = (contactId: string) => {
		setIsViewDonationOpen(true);
		setContactIdOfDonorToView(contactId);
	};

	const onDeleteConfirmed = () => {
		setIsDeleteDonationOpen(false);
		refreshDonations();
	};

	const onExport = () => {
		if (isDonorMode) {
			exportDonationsByDonorsMutation.mutate({
				filterRequest: {
					...unifiedFilterRequest,
					...(donorselectionState === Api.ESelectionState.Some
						? { includeIds: selectedDonors }
						: donorselectionState === Api.ESelectionState.All
							? { excludeIds: excludedDonors }
							: {}),
				},
			});
		} else {
			exportDonationsMutation.mutate({
				filterRequest: {
					...unifiedFilterRequest,
					...(donationSelectionState === Api.ESelectionState.Some
						? { includeIds: selectedDonations }
						: donationSelectionState === Api.ESelectionState.All
							? { excludeIds: excludedDonations }
							: {}),
				},
			});
		}
	};

	const canEdit = !userSession.hasNonProfitIntegrationConfigured;

	const hasSelectionForDelete = isDonationSelectionStateAll || selectedDonations.length > 0;
	const canBulkDelete = canEdit && !isDonorMode && hasSelectionForDelete;

	// When select-all is active with exclusions, we can't honor exclusions on the server-side
	// bulk-delete-by-filter path (no donation-id filter property). Fall back to per-donation
	// delete of loaded rows minus exclusions, and reflect that in the count.
	const isSelectAllWithExclusions = isDonationSelectionStateAll && excludedDonations.length > 0;
	const loadedDonationIdsForBulkDelete = React.useMemo(
		() => (isSelectAllWithExclusions ? donations.filter(d => !excludedDonations.includes(d.id)).map(d => d.id) : []),
		[isSelectAllWithExclusions, donations, excludedDonations]
	);

	const bulkDeleteCount = React.useMemo(() => {
		if (!canBulkDelete) return 0;
		if (isSelectAllWithExclusions) return loadedDonationIdsForBulkDelete.length;
		if (isDonationSelectionStateAll) return donationSummaryQuery.data?.totalDonations ?? 0;
		return selectedDonations.length;
	}, [
		canBulkDelete,
		isDonationSelectionStateAll,
		isSelectAllWithExclusions,
		loadedDonationIdsForBulkDelete.length,
		donationSummaryQuery.data?.totalDonations,
		selectedDonations.length,
	]);

	const bulkDeleteMutation = useBulkDeleteDonationsMutation();
	const deleteDonationMutation = useDeleteDonationMutation();

	const confirmBulkDelete = React.useCallback(async (): Promise<number> => {
		if (isDonationSelectionStateAll && excludedDonations.length === 0) {
			const result = await bulkDeleteMutation.mutateAsync(unifiedFilterRequest);
			return result?.deletedCount ?? 0;
		}
		const ids = isDonationSelectionStateAll ? loadedDonationIdsForBulkDelete : [...selectedDonations];
		const results = await Promise.allSettled(ids.map(donationId => deleteDonationMutation.mutateAsync({ donationId })));
		const succeededIds = ids.filter((_, i) => results[i].status === 'fulfilled');
		if (succeededIds.length < ids.length) {
			throw new Error(`Only ${succeededIds.length} of ${ids.length} donations could be deleted. Please try again.`);
		}
		return succeededIds.length;
	}, [
		isDonationSelectionStateAll,
		excludedDonations.length,
		unifiedFilterRequest,
		loadedDonationIdsForBulkDelete,
		selectedDonations,
		bulkDeleteMutation,
		deleteDonationMutation,
	]);

	const onEntityClicked = (contactId: string, e: React.MouseEvent<HTMLDivElement>) => {
		const path = `${window.location.origin}/#/people/${contactId}`;
		if (e.ctrlKey || e.metaKey) {
			openUrlInNewTab(path);
			e.preventDefault();
		}
		// Else let the EntityChip handle the click
	};

	const isLoading = isDonorMode ? donorFilterQuery.isPending : donationFilterQuery.isPending;
	const noResultsFound =
		!isLoading && isDonorMode
			? donorFilterQuery.isFetched && donors.length === 0
			: donationFilterQuery.isFetched && donations.length === 0;

	const openAdvancedReporting = () => {
		history.push(ROUTES.dataBoards.donationsAdvancedReporting);
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-donation-board',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(c => new Api.ContactViewModel(userSession, c)),
						donationFilter: !isDonorMode ? unifiedFilterRequest : null,
					},
					type: 'Contacts',
				},
			},
		});
	};

	const onTaxLetterClicked = async () => {
		const matchingContacts = await getMatchingContacts();
		setBulkTaxLetterContactIds(matchingContacts.map(c => c.id));
	};

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Donations', path: ROUTES.dataBoards.donations },
						]}
						banner={
							<>
								<div className={css(styleSheet.banner)}>
									<Tooltip>
										<TooltipTrigger asChild>
											<a
												href='#'
												className={css(styleSheet.bannerLink)}
												onClick={ev => {
													ev.preventDefault();
													openAdvancedReporting();
												}}
											>
												<SmallGraph />
												<span className={css(styleSheet.bannerLinkText)}>
													{' '}
													Check our advanced reporting{' '}
													<DoubleArrowIcon fillColor={brandSecondary} style={{ marginLeft: '0.5rem' }} />
												</span>
											</a>
										</TooltipTrigger>
										<TooltipContent>Check our advanced reporting</TooltipContent>
									</Tooltip>
								</div>
								{!canEdit && (
									<div className={css(styleSheet.viewOnlyContainer)}>
										<Tooltip>
											<TooltipTrigger asChild>
												<span className={css(styleSheet.viewOnlyBadge)}>
													<EyeIcon height={20} fillColor={THEME.colors.text.light} />
													<span className={css(styleSheet.viewOnlyBadgeText)}>View Only Mode</span>
												</span>
											</TooltipTrigger>
											<TooltipContent>
												When DonorPerfect is enabled you cannot make edits to the donation data through Levitate.
											</TooltipContent>
										</Tooltip>
									</div>
								)}
							</>
						}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.databoardContainer]} key={key}>
					<DataboardFilters>
						<div className={css(dbStyleSheet.propertySearch)}>
							<Select
								value={selectedSearchProperty.id}
								onValueChange={id => {
									const opt = DonationPropertyOptions.find(o => o.id === id);
									if (opt) selectSearchProperty(opt);
								}}
							>
								<SelectTrigger styles={[dbStyleSheet.searchPropertySelectorResponsive]}>
									{selectedSearchProperty.text}
								</SelectTrigger>
								<SelectContent>
									{DonationPropertyOptions.map(opt => (
										<SelectItem key={opt.id} value={opt.id}>
											{opt.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{selectedSearchProperty.text === DonationPropertyOption.DonorName ? (
								<TextInput
									inputId='donor-name-filter-input'
									type='text'
									className={css(dbStyleSheet.comboBoxRightAutocompleteResponsive)}
									inputClassName={css(dbStyleSheet.wideInputResponsive)}
									onChange={onSearchFieldInputChanged}
									value={searchFieldValue}
									leftAccessory={<SearchIcon className={css(dbStyleSheet.searchIcon)} />}
									placeholder='Search'
								/>
							) : null}

							{selectedSearchProperty.text === DonationPropertyOption.Campaign ? (
								<DonationCampaignSearch
									className={css(dbStyleSheet.comboBoxRight, dbStyleSheet.wideInput, dbStyleSheet.searchFieldReset)}
									onSelectDonationCampaign={selectDonationCampaign}
									dropdownClassName={css(dbStyleSheet.comboBoxDropdown)}
									canAdd={false}
									onClear={onCampaignSearchClear}
								/>
							) : null}

							{selectedSearchProperty.text === DonationPropertyOption.Tags ? (
								<DonationTagSearch
									inputStyle={[
										simpleAutoCompleteStyleSheet.searchFieldWhiteBackground,
										dbStyleSheet.comboBoxRightAutocompleteResponsive,
										dbStyleSheet.wideInputResponsive,
									]}
									onTagSelected={setSelectedTag}
									onClear={() => setSelectedTag(null)}
									selectedTag={selectedTag}
								/>
							) : null}
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<AmountFilterDropdown
								key={`amountFilterDropdown-${selectedAmountFilter}-${minimumDonation}-${maximumDonation}`}
								label={amountFilterDropdownText}
								initialAmountFilterOption={selectedAmountFilter}
								initialMinimumDonation={minimumDonation}
								initialMaximumDonation={maximumDonation}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
								onChange={value => {
									setSelectedAmountFilter(value.amountFilterOption);
									setMinimumDonation(value.minimumDonation);
									setMaximumDonation(value.maximumDonation);
								}}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<DonationDateDropdown
								label='Has Donated'
								minimumDate={hasDonatedMinDate}
								setMinimumDate={setHasDonatedMinDate}
								maximumDate={hasDonatedMaxDate}
								setMaximumDate={setHasDonatedMaxDate}
								showFirstDonationCheckbox
								isFirstDonation={isFirstDonation}
								setIsFirstDonation={setIsFirstDonation}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>
						<div className={css(dbStyleSheet.mediumInput)}>
							<DonationDateDropdown
								label='Has NOT Donated'
								minimumDate={notDonatedMinDate}
								setMinimumDate={setNotDonatedMinDate}
								maximumDate={notDonatedMaxDate}
								setMaximumDate={setNotDonatedMaxDate}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInputNoShrink)}>
							<Select
								value={selectedViewMode}
								onValueChange={id => {
									setSelectedViewMode(id as 'donors' | 'donations');
									setUserHasSelectedViewMode(true);
								}}
							>
								<SelectTrigger styles={[dbStyleSheet.dropdownReset]}>
									{`Show: ${selectedViewMode === 'donors' ? 'Donors' : 'Donations'}`}
								</SelectTrigger>
								<SelectContent>
									{availableViewModes.map(mode => (
										<SelectItem key={mode} value={mode}>
											{mode === 'donors' ? 'Donors' : 'Donations'}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</DataboardFilters>

					<DonationSearchDescription
						totalResults={isDonorMode ? summary?.totalDonors : summary?.totalDonations}
						viewMode={selectedViewMode}
						searchFieldValue={debouncedSearchFieldValue}
						selectedCampaign={selectedCampaign}
						selectedTag={selectedTag}
						selectedAmountFilter={selectedAmountFilter}
						minimumDonation={minimumDonation}
						maximumDonation={maximumDonation}
						hasDonatedMinDate={hasDonatedMinDate}
						hasDonatedMaxDate={hasDonatedMaxDate}
						isFirstDonation={isFirstDonation}
						notDonatedMinDate={notDonatedMinDate}
						notDonatedMaxDate={notDonatedMaxDate}
						onClearSearch={clearSearch}
					/>

					{creationDateRange && (
						<div className={css(styleSheet.creationDateChipRow)}>
							<Pill
								variant='label'
								color='blue'
								size='medium'
								label={`Created ${moment(creationDateRange.startIso).format('MMM D, YYYY')} – ${moment(
									creationDateRange.endIso
								).format('MMM D, YYYY')}`}
								onRemove={() => setCreationDateRange(null)}
							/>
						</div>
					)}

					<DataboardActions>
						<div className={css(dbStyleSheet.leftHeader)}>
							<UsersActionBar
								excludedContacts={isDonorMode ? donorExcludedContacts : donationsExcludedContacts}
								getMatchingContacts={getMatchingContacts}
								onExport={onExport}
								selectionState={isDonorMode ? donorselectionState : donationSelectionState}
								actionButtonsDisabled={actionButtonsDisabled}
								onSendEmailClicked={onSendEmailClicked}
								onTaxLetterClicked={onTaxLetterClicked}
								onDeleteClicked={canEdit && !isDonorMode ? () => setIsBulkDeleteOpen(true) : undefined}
								deleteDisabled={!canBulkDelete}
							/>
						</div>

						{canEdit ? (
							<div className={css(dbStyleSheet.rightHeader)}>
								<div className={css(dbStyleSheet.addButtonContainer)}>
									<button
										className={css(baseStyleSheet.ctaButton, dbStyleSheet.addButton)}
										onClick={addDonationClicked}
									>
										<span>Add Donation</span>
									</button>
									<Popover>
										<PopoverTrigger asChild>
											<button className={css(dbStyleSheet.dropdownCaret)}>
												<DropDownIcon fillColor={white} />
											</button>
										</PopoverTrigger>
										<PopoverContent align='end' styles={[styleSheet.addDonationBtnPopover]}>
											<div className={css(styleSheet.importMenuContainer)}>
												<button
													className={css(styleSheet.importMenuButton, styleSheet.importMenuButtonPrimary)}
													onClick={importClicked}
												>
													Import from CSV
												</button>
												<div className={css(styleSheet.importMenuDivider)} />
												<button
													className={css(styleSheet.importMenuButton, styleSheet.importMenuButtonSecondary)}
													onClick={() => setIsDeletePreviousImportOpen(true)}
												>
													Delete previous import
												</button>
											</div>
										</PopoverContent>
									</Popover>
								</div>
							</div>
						) : null}
					</DataboardActions>

					<DataboardBody data-scroll-restoration-id='donations-page-table' hasFooter>
						<DataGrid styles={[dbStyleSheet.tableOverrides]} isLoading={isLoading}>
							<DataGridColumns>
								<DataGridColumn sticky styles={[dbStyleSheet.checkboxColumn]}>
									<Checkbox
										checked={isDonorMode ? isDonorSelectionStateAll : isDonationSelectionStateAll}
										id='select-all-donations'
										onChange={onToggleSelectAll}
										partial={isPartialSelected}
									/>
								</DataGridColumn>
								<DataGridColumn sticky>Donor Name</DataGridColumn>
								<DataGridColumn sticky>Contact</DataGridColumn>
								<DataGridColumn sticky>{!isDonorMode ? 'Donation Date' : 'Donation Count'}</DataGridColumn>
								<DataGridColumn sticky>{isDonorMode ? 'Lifetime Donations' : 'Amount'}</DataGridColumn>
								{!isDonorMode ? <DataGridColumn sticky>Campaign</DataGridColumn> : null}
								{!isDonorMode ? <DataGridColumn sticky>Tags</DataGridColumn> : null}
								<DataGridColumn sticky>&nbsp;</DataGridColumn>
							</DataGridColumns>

							<DataGridRows>
								{!isDonorMode && !isLoading
									? donations.map(donation => (
											<React.Fragment key={`donation-${donation.id}`}>
												<DataGridRow
													styles={[
														dbStyleSheet.row,
														styleSheet.donationRow,
														donation.details && styleSheet.rowWithDetails,
													]}
												>
													<DataGridRowItem>
														<Checkbox
															readOnly
															checked={isDonationSelected(donation.id)}
															id={donation.id}
															onClick={event => {
																event.stopPropagation();
																event.preventDefault();
																toggleDonationRow(donation.id, event.shiftKey);
															}}
														/>
													</DataGridRowItem>
													<DataGridRowItem>
														<button onClick={() => viewDetails(donation.contactId)}>
															<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>
																{donation.donorName}
															</h6>
														</button>
													</DataGridRowItem>
													<DataGridRowItem>
														{donation.contact ? (
															<EntityChip
																className={css(dbStyleSheet.contact)}
																key={donation.contact.id}
																entity={new Api.ContactViewModel(userSession, donation.contact)}
																readOnly={true}
																onClick={(e: React.MouseEvent<HTMLDivElement>) =>
																	onEntityClicked(donation.contactId, e)
																}
																sizeConstraint='compact'
															/>
														) : null}
													</DataGridRowItem>
													<DataGridRowItem>{moment(donation.date).format('MM/DD/YYYY')}</DataGridRowItem>
													<DataGridRowItem styles={[dbStyleSheet.amountText]}>
														<div>
															<div className={css(dbStyleSheet.amountText)}>
																${numberToCurrencyStringValue(donation.amount)}
															</div>
															{donation.isInKindDonation && donation.inKindDescription && (
																<div className={css(styleSheet.inKindDescription)}>{donation.inKindDescription}</div>
															)}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														{donation.campaign ? (
															<Badge
																text={donation.campaign.name}
																foregroundColor={donation.campaign.foregroundColor}
															/>
														) : null}
													</DataGridRowItem>
													<DataGridRowItem>
														<div className={css(dbStyleSheet.tagsColumn)}>
															{(() => {
																// Use tagsCollection if available (with colors), otherwise fall back to tags array
																const displayTags =
																	donation.tagsCollection && donation.tagsCollection.length > 0
																		? donation.tagsCollection
																		: donation.tags?.map(tagName => ({
																				name: tagName,
																				id: tagName,
																				color: undefined as Api.IAccountTag['color'],
																			})) || [];

																if (displayTags.length > 0) {
																	return (
																		<>
																			<Pill
																				variant='tag'
																				label={displayTags[0].name}
																				color={'color' in displayTags[0] ? displayTags[0].color : undefined}
																			/>
																			{displayTags.length > 1 && (
																				<HoverCard>
																					<HoverCardTrigger
																						className={css(baseStyleSheet.brandLink, dbStyleSheet.tagMore)}
																					>
																						&nbsp;
																						{`+${displayTags.length - 1} more`}
																					</HoverCardTrigger>
																					<HoverCardContent styles={[dbStyleSheet.extraTags]}>
																						{displayTags.slice(1).map(tagValue => {
																							return (
																								<Pill
																									key={'id' in tagValue ? tagValue.id : tagValue.name}
																									variant='tag'
																									label={tagValue.name}
																									color={'color' in tagValue ? tagValue.color : undefined}
																								/>
																							);
																						})}
																					</HoverCardContent>
																				</HoverCard>
																			)}
																		</>
																	);
																}
																return null;
															})()}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<Dropdown>
															<DropdownEllipsisTrigger />
															<DropdownContent>
																{canEdit ? (
																	<>
																		<DropdownItem onClick={() => editDonation(donation)}>{MenuItem.Edit}</DropdownItem>
																		<DropdownItem onClick={() => deleteDonation(donation)}>
																			{MenuItem.Delete}
																		</DropdownItem>
																	</>
																) : null}
																<DropdownItem
																	onClick={() =>
																		setTaxLetterTarget({
																			contactId: donation.contactId,
																			donationIds: [donation.id],
																		})
																	}
																>
																	{MenuItem.TaxLetter}
																</DropdownItem>
															</DropdownContent>
														</Dropdown>
													</DataGridRowItem>
												</DataGridRow>
												{donation.details && (
													<DataGridRow styles={[styleSheet.detailsRow]}>
														<DataGridRowItem colSpan={4} styles={[styleSheet.detailsSpacerCell]} />
														<DataGridRowItem colSpan={4} styles={[styleSheet.detailsCell]}>
															<ExpandableText text={donation.details} prefix='Donation Details: ' />
														</DataGridRowItem>
													</DataGridRow>
												)}
											</React.Fragment>
										))
									: null}

								{isDonorMode && !isLoading
									? donors.map(donor => (
											<DataGridRow key={`donor-${donor.contactId}`}>
												<DataGridRowItem>
													<Checkbox
														readOnly
														checked={isDonorSelected(donor.contactId)}
														id={donor.contactId}
														onClick={event => {
															event.stopPropagation();
															event.preventDefault();
															toggleDonorRow(donor.contactId, event.shiftKey);
														}}
													/>
												</DataGridRowItem>
												<DataGridRowItem>
													<button onClick={() => viewDetails(donor.contactId)}>
														<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>{donor.donorName}</h6>
													</button>
												</DataGridRowItem>
												<DataGridRowItem>
													{donor.contact ? (
														<EntityChip
															className={css(dbStyleSheet.contact)}
															key={donor.contact.id}
															entity={new Api.ContactViewModel(userSession, donor.contact)}
															readOnly={true}
															onClick={(e: React.MouseEvent<HTMLDivElement>) => onEntityClicked(donor.contactId, e)}
															sizeConstraint='compact'
														/>
													) : null}
												</DataGridRowItem>
												<DataGridRowItem>{donor.donationCount}</DataGridRowItem>
												<DataGridRowItem styles={[dbStyleSheet.amountText]}>
													${numberToCurrencyStringValue(donor.lifetimeDonations)}
												</DataGridRowItem>
												<DataGridRowItem>
													<Dropdown>
														<DropdownEllipsisTrigger />
														<DropdownContent>
															<DropdownItem
																onClick={() =>
																	setTaxLetterTarget({
																		contactId: donor.contactId,
																	})
																}
															>
																{MenuItem.TaxLetter}
															</DropdownItem>
														</DropdownContent>
													</Dropdown>
												</DataGridRowItem>
											</DataGridRow>
										))
									: null}

								{isLoading ? (
									<DataGridRow>
										<DataGridRowItem colSpan={8}>
											<LoadingSpinner type='large' />
										</DataGridRowItem>
									</DataGridRow>
								) : null}
							</DataGridRows>
						</DataGrid>
						{isDonorMode && !donorFilterQuery.isPending && donorFilterQuery.hasNextPage ? (
							<Waypoint bottomOffset='-200px' onEnter={() => donorFilterQuery.fetchNextPage()} />
						) : null}
						{!isDonorMode && !donationFilterQuery.isPending && donationFilterQuery.hasNextPage ? (
							<Waypoint bottomOffset='-200px' onEnter={() => donationFilterQuery.fetchNextPage()} />
						) : null}
						{noResultsFound ? <DataboardNoResults onClearSearch={clearSearch} /> : null}
					</DataboardBody>
					<DataboardStats>
						<DataboardStatTitle>Donation Stats</DataboardStatTitle>
						<DataboardStat>
							<DataboardStatLabel># Donors</DataboardStatLabel>
							<DataboardStatValue>{summary?.totalDonors || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel># Donations</DataboardStatLabel>
							<DataboardStatValue>{summary?.totalDonations || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Total</DataboardStatLabel>
							<DataboardStatValue isTotal>${numberToCurrencyStringValue(summary?.totalAmount || 0)}</DataboardStatValue>
						</DataboardStat>
					</DataboardStats>

					{donationToEdit ? (
						<EditDonationModal
							initialDonation={donationToEdit}
							isOpen={isEditDonationOpen}
							setIsOpen={setIsEditDonationOpen}
							onSave={refreshDonations}
							onCancel={refreshDonations}
						/>
					) : null}

					{contactIdOfDonorToView ? (
						<DonorDetailsFlyout
							isOpen={isViewDonationOpen}
							setIsOpen={setIsViewDonationOpen}
							contactId={contactIdOfDonorToView}
						/>
					) : null}

					{donationToDelete ? (
						<DeleteModal
							type={DeleteType.Donation}
							item={donationToDelete}
							isOpen={isDeleteDonationOpen}
							setIsOpen={setIsDeleteDonationOpen}
							onConfirm={onDeleteConfirmed}
						/>
					) : null}

					{taxLetterTarget ? (
						<TaxLetterModal
							isOpen={!!taxLetterTarget}
							onClose={() => setTaxLetterTarget(null)}
							mode='single'
							contactId={taxLetterTarget.contactId}
							donationIds={taxLetterTarget.donationIds}
						/>
					) : null}

					{bulkTaxLetterContactIds ? (
						<TaxLetterModal
							isOpen={!!bulkTaxLetterContactIds}
							onClose={() => setBulkTaxLetterContactIds(null)}
							mode='bulk'
							contactIds={bulkTaxLetterContactIds}
						/>
					) : null}

					<ExportConfirmationModalV2
						mutationResult={isDonorMode ? exportDonationsByDonorsMutation : exportDonationsMutation}
						modalProps={{
							isOpen: isDonorMode ? !exportDonationsByDonorsMutation.isIdle : !exportDonationsMutation.isIdle,
							onRequestClose: () =>
								isDonorMode ? exportDonationsByDonorsMutation.reset() : exportDonationsMutation.reset(),
						}}
						subTitle={
							<>
								<p className={css(dbStyleSheet.noMargin)}>
									{isDonorMode
										? 'The fields we’ll export include: Donor Name, Contact Id, Primary Email, Phone Number, Address, Lifetime Donations, Most Recent Donation Amount, Most Recent Donation Date, Most Recent Donation Campaign Name'
										: 'The fields we’ll export include: Donor Name, Contact Id, Primary Email, Phone Number, Address, Amount, Date, Campaign Name, Tags, In-Kind Description, Is In-Kind Donation, Details'}
								</p>
							</>
						}
					/>

					<DeletePreviousImportModal
						isOpen={isDeletePreviousImportOpen}
						onClose={() => setIsDeletePreviousImportOpen(false)}
						onApplyRange={range => {
							resetDonationSelection();
							resetDonorselection();
							setCreationDateRange(range);
							setSelectedViewMode('donations');
							setUserHasSelectedViewMode(true);
						}}
						onWipedAll={() => {
							resetDonationSelection();
							resetDonorselection();
							setCreationDateRange(null);
							invalidateAllDonationFilters();
							invalidateAllDonorFilters();
						}}
					/>

					<DeleteDonationsConfirmModal
						isOpen={isBulkDeleteOpen}
						onClose={() => setIsBulkDeleteOpen(false)}
						count={bulkDeleteCount}
						onConfirm={confirmBulkDelete}
						onDeleted={async () => {
							resetDonationSelection();
							await Promise.all([invalidateAllDonationFilters(), invalidateAllDonorFilters()]);
						}}
					/>

					<FabContext appearance={{ hidden: true }} />
				</Databoard>
			</Column>
		</Page>
	);
}
